import { createRef, useEffect, useState } from 'react';
import Dropzone, { DropzoneRef } from 'react-dropzone';
import { Box, Text } from '@hopskipdrive/react-components';
import { FilePresentOutlined } from '@mui/icons-material';
import { Button } from '@rideiq/shared/components/Button';

import { colorTokens } from '../variables';

import { DropzoneContainer } from './styles';

interface FileUploadDropzoneProps {
  handleAcceptedFiles: (acceptedFiles: File[]) => void;
}

export const FileUploadDropzone: React.FC<FileUploadDropzoneProps> = ({ handleAcceptedFiles }) => {
  const dropzoneRef = createRef<DropzoneRef>();
  const [acceptedFiles, setAcceptedFiles] = useState<File[]>([]);
  const openDialog = () => {
    if (dropzoneRef.current) {
      dropzoneRef.current.open();
    }
  };

  useEffect(() => {
    if (acceptedFiles.length) {
      handleAcceptedFiles([...acceptedFiles]);
    }
  }, [acceptedFiles, handleAcceptedFiles]);

  return (
    <Dropzone ref={dropzoneRef} noClick noKeyboard onDropAccepted={setAcceptedFiles}>
      {({ getRootProps, getInputProps }) => {
        const borderColor = acceptedFiles.length !== 0 ? 'rgba(76, 175, 80, 1)' : 'rgba(0, 0, 0, 0.26)';
        const borderImage = `"data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='5' ry='5' stroke='${borderColor}' stroke-width='2' stroke-dasharray='10' stroke-dashoffset='0' stroke-linecap='round'/%3e%3c/svg%3e"`;

        return (
          <DropzoneContainer {...getRootProps()} borderImage={borderImage}>
            <input {...getInputProps()} data-testid="drop-input" />
            {!acceptedFiles.length && (
              <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                <Text variant="labelXL" textColor="neutral70" mb={3} textAlign="center">
                  Drag and drop your .CSV file here <br /> or
                </Text>
                <Button color="secondary" size="medium" onClick={openDialog} style={{ maxWidth: '110px' }}>
                  Browse files
                </Button>
              </Box>
            )}
            {acceptedFiles.map((file) => (
              <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" key={file.name}>
                <Box display="flex" alignItems="center" mb={3}>
                  <FilePresentOutlined htmlColor={colorTokens.text.active} sx={{ height: '16px', width: '16px' }} />
                  <Text variant="bodyM" textColor="secondary-active" ml={2}>
                    {`${file.name} (${Math.round(file.size / 1000)}kb)`}
                  </Text>
                </Box>
                <Button color="secondary" size="medium" onClick={openDialog} style={{ maxWidth: '84px' }}>
                  Replace
                </Button>
              </Box>
            ))}
          </DropzoneContainer>
        );
      }}
    </Dropzone>
  );
};
